import axios from 'axios';

interface CacheEntry<T> {
    data: T;
    expiresAt: number;
}

const responseCache = new Map<string, CacheEntry<unknown>>();
const pendingRequests = new Map<string, Promise<unknown>>();
const MAX_ENTRIES = 150;
let cacheVersion = 0;

export async function cachedGet<T>(url: string, ttlMs = 30_000): Promise<T> {
    const now = Date.now();
    const cached = responseCache.get(url) as CacheEntry<T> | undefined;
    if (cached && cached.expiresAt > now) {
        responseCache.delete(url);
        responseCache.set(url, cached);
        return cached.data;
    }

    const pending = pendingRequests.get(url) as Promise<T> | undefined;
    if (pending) return pending;

    const requestVersion = cacheVersion;
    const request: Promise<T> = axios.get<T>(url)
        .then((response) => {
            // A response that started before logout/login must never populate
            // the next browser session's cache.
            if (requestVersion === cacheVersion) {
                responseCache.set(url, { data: response.data, expiresAt: Date.now() + ttlMs });
                trimCache();
            }
            return response.data;
        })
        .finally(() => {
            // An invalidation may have started a newer request for this URL.
            // The older promise must not remove that newer in-flight entry.
            if (pendingRequests.get(url) === request) pendingRequests.delete(url);
        });

    pendingRequests.set(url, request);
    return request;
}

export function clearResponseCache() {
    cacheVersion += 1;
    responseCache.clear();
    pendingRequests.clear();
}

export function invalidateResponseCache(prefix: string) {
    // Prevent requests started before the manual refresh from repopulating the
    // cache, then release matching in-flight entries so a fresh call can start.
    cacheVersion += 1;
    for (const key of responseCache.keys()) {
        if (key.includes(prefix)) responseCache.delete(key);
    }
    for (const key of pendingRequests.keys()) {
        if (key.includes(prefix)) pendingRequests.delete(key);
    }
}

function trimCache() {
    while (responseCache.size > MAX_ENTRIES) {
        const oldestKey = responseCache.keys().next().value as string | undefined;
        if (!oldestKey) break;
        responseCache.delete(oldestKey);
    }
}
